/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  const quickAddStyleRegistry = new Map();
  const unusableExternalStyleLinks = new WeakSet();

  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productRequests = new Map();
      this.maxCachedProducts = 6;
      this.stylePromise = null;
      this.activeRequest = 0;
      this.quickAddIntentHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);
      this.pageShowHandler = this.handlePageShow.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.addEventListener('pointerover', this.quickAddIntentHandler, { passive: true });
      }
      document.addEventListener('pointerdown', this.quickAddIntentHandler, { passive: true });
      document.addEventListener('touchstart', this.quickAddIntentHandler, { passive: true });
      document.addEventListener('focusin', this.quickAddIntentHandler);
      window.addEventListener('pageshow', this.pageShowHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.removeEventListener('pointerover', this.quickAddIntentHandler);
      document.removeEventListener('pointerdown', this.quickAddIntentHandler);
      document.removeEventListener('touchstart', this.quickAddIntentHandler);
      document.removeEventListener('focusin', this.quickAddIntentHandler);
      window.removeEventListener('pageshow', this.pageShowHandler);
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      const button = evt.target.closest?.('.js-quick-add');
      if (!button?.dataset.productUrl) return;

      this.prepareStyles().catch(() => {});
      this.fetchProduct(button.dataset.productUrl).catch(() => {});
    }

    /**
     * Loads the drawer styles once, with retry after an error or timeout.
     * @returns {Promise<Array<HTMLLinkElement>>}
     */
    prepareStyles() {
      const urls = (this.dataset.styleUrls || '').split('|').map((url) => url.trim()).filter(Boolean);
      if (urls.length === 0) return Promise.resolve([]);

      // Le registre statique déduplique les appels simultanés et revalide les
      // liens déjà résolus. Repasser par lui permet de recréer une feuille qui
      // aurait été retirée lors d'un rerender ou d'une restauration bfcache.
      this.stylePromise = Promise.all(urls.map((url) => QuickAddDrawer.loadStyle(url))).catch((error) => {
        this.stylePromise = null;
        throw error;
      });
      return this.stylePromise;
    }

    /**
     * Loads one interactive stylesheet without mutating or removing an
     * unrelated conditional link. Requests are shared by all drawer instances.
     * @param {string} url - Stylesheet URL.
     * @param {number} timeoutMs - Maximum wait before allowing a retry.
     * @returns {Promise<HTMLLinkElement>}
     */
    static loadStyle(url, timeoutMs = 3500) {
      const href = new URL(url, document.baseURI).href;
      const cachedPromise = quickAddStyleRegistry.get(href);
      if (cachedPromise) {
        const cachedLink = cachedPromise.quickAddStyleLink;
        const cachedOwned = cachedLink?.dataset?.dgQuickAddStyle !== undefined;
        const cachedThemeDeferred = cachedLink?.dataset?.dgThemeAsset !== undefined;
        const cachedMediaIsUsable = cachedOwned || cachedThemeDeferred
          ? cachedLink?.media !== 'print'
          : cachedLink?.media === '' || cachedLink?.media === 'all';
        if (cachedLink?.isConnected && !cachedLink.disabled && cachedMediaIsUsable) {
          return cachedPromise;
        }
        quickAddStyleRegistry.delete(href);
      }

      let registryPromise = null;
      let resolvedLink = null;
      const rawPromise = new Promise((resolve, reject) => {
        let link = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
          .find((candidate) => {
            if (candidate.href !== href || candidate.disabled || unusableExternalStyleLinks.has(candidate)) {
              return false;
            }
            const themeOwned = candidate.dataset.dgQuickAddStyle !== undefined
              || candidate.dataset.dgThemeAsset !== undefined;
            return themeOwned || candidate.media === '' || candidate.media === 'all';
          });
        let removable = link?.dataset?.dgQuickAddStyle !== undefined;
        const activatable = removable || link?.dataset?.dgThemeAsset !== undefined;
        let settled = false;
        let timer = null;

        const cleanup = () => {
          if (timer) window.clearTimeout(timer);
          if (!link) return;
          link.removeEventListener('load', succeed);
          link.removeEventListener('error', fail);
        };

        const succeed = () => {
          if (settled || !link) return;
          settled = true;
          cleanup();
          if (activatable || removable) {
            link.media = 'all';
            link.disabled = false;
          }
          if (removable) link.dataset.dgQuickAddStyleState = 'loaded';
          resolve(link);
        };

        const fail = () => {
          if (settled) return;
          settled = true;
          cleanup();
          if (link && removable) {
            link.dataset.dgQuickAddStyleState = 'error';
            link.remove();
          } else if (link) {
            unusableExternalStyleLinks.add(link);
          }
          reject(new Error(`Unable to load stylesheet: ${href}`));
        };

        if (!link) {
          link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.media = 'all';
          link.dataset.dgQuickAddStyle = '';
          link.dataset.dgQuickAddStyleState = 'loading';
          removable = true;
        }

        resolvedLink = link;
        link.addEventListener('load', succeed, { once: true });
        link.addEventListener('error', fail, { once: true });
        timer = window.setTimeout(fail, timeoutMs);

        if (link.sheet) {
          succeed();
        } else if (!link.isConnected) {
          document.head.appendChild(link);
        }
      });

      registryPromise = rawPromise.catch((error) => {
        if (quickAddStyleRegistry.get(href) === registryPromise) quickAddStyleRegistry.delete(href);
        throw error;
      });
      registryPromise.quickAddStyleLink = resolvedLink;
      quickAddStyleRegistry.set(href, registryPromise);
      return registryPromise;
    }

    /**
     * Fetches and caches validated product HTML. Failed requests remain retryable.
     * @param {string} url - Product URL.
     * @returns {Promise<string>}
     */
    fetchProduct(url) {
      if (this.productRequests.has(url)) {
        const cachedRequest = this.productRequests.get(url);
        // Refresh insertion order so the small cache behaves as an LRU.
        this.productRequests.delete(url);
        this.productRequests.set(url, cachedRequest);
        return cachedRequest;
      }

      const request = fetch(url).then(async (response) => {
        if (!response.ok) throw new Error(`Quick Add request failed (${response.status})`);
        return response.text();
      }).catch((error) => {
        if (this.productRequests.get(url) === request) this.productRequests.delete(url);
        throw error;
      });

      this.productRequests.set(url, request);
      while (this.productRequests.size > this.maxCachedProducts) {
        this.productRequests.delete(this.productRequests.keys().next().value);
      }
      return request;
    }

    /**
     * Extracts a fresh product node from a cached HTML response.
     * @param {string} html - Product page HTML.
     * @returns {HTMLElement}
     */
    static parseProduct(html) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const quickBuyTemplate = tmpl.content.getElementById('quick-buy-template');
      const product = quickBuyTemplate?.content?.querySelector('.js-product');
      if (!product) throw new Error('Quick Add template is missing');
      return product;
    }

    /**
     * Clears transient state restored by the browser back-forward cache.
     * @param {PageTransitionEvent} evt - pageshow event.
     */
    handlePageShow(evt) {
      if (!evt.persisted) return;
      this.activeRequest += 1;
      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
      this.footer.classList.remove('drawer__footer--out');
      if (this.getAttribute('aria-hidden') === 'false') this.close();
      document.querySelectorAll('.js-quick-add[aria-disabled="true"]').forEach((button) => {
        button.removeAttribute('aria-disabled');
      });
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      const button = evt.target.closest?.('.js-quick-add');
      if (!button) return;
      if (button.getAttribute?.('aria-disabled') === 'true') return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        button.setAttribute('aria-disabled', 'true');
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(button);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(button);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      const productUrl = opener.dataset.productUrl;
      if (!productUrl) return;

      const requestId = ++this.activeRequest;
      const alreadyRendered = this.productUrl === productUrl && this.content.hasChildNodes();
      const productRequest = alreadyRendered ? null : this.fetchProduct(productUrl);
      // Keep a rejection handled if CSS fails first or another click wins the race.
      if (productRequest) productRequest.catch(() => {});
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      try {
        await this.prepareStyles();
        if (requestId !== this.activeRequest) return;

        // If it's the same product as previously shown, there's no need to re-fetch the details.
        if (alreadyRendered) {
          super.open(opener);
          return;
        }

        this.content.innerHTML = '';
        this.classList.add('is-loading');
        this.content.classList.add('drawer__content--out');
        this.footer.classList.add('drawer__footer--out');
        super.open(opener);

        const html = await productRequest;
        if (requestId !== this.activeRequest) return;

        this.productEl = QuickAddDrawer.parseProduct(html);
        this.productUrl = productUrl;
        this.renderProduct(opener);
      } catch (error) {
        if (requestId !== this.activeRequest) return;
        this.productRequests.delete(productUrl);
        this.productUrl = '';
        this.classList.remove('is-loading');
        this.content.classList.remove('drawer__content--out');
        this.footer.classList.remove('drawer__footer--out');
        if (this.getAttribute('aria-hidden') === 'false') this.close();
        window.location.assign(productUrl);
      } finally {
        opener.removeAttribute('aria-disabled');
      }
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

      // Check whether to select the first variant.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      this.updateFooter();
      this.updateContent();

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} media - Variant featured media object.
     */
    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    /**
     * Renders the markup for the drawer content element.
     */
    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}
